import type { ComponentType } from 'react'
import { Icons } from '@/components/ui/icons'
import { canAccess } from '@/lib/roles'

import { breadcrumbTrail, crumbLabelFor, type Crumb } from '@/lib/nav-paths'

export { crumbLabelsVersion, isNavItemActive, setCrumbLabel, subscribeCrumbLabels } from '@/lib/nav-paths'

/**
 * The product line under "Supervity" in the rail and the reviewer shell
 * (plan 08 WP-VIS-02). Presentation only, so an env switch rather than a Setting.
 */
export const AUTO_NAME = process.env.NEXT_PUBLIC_AUTO_NAME || 'Accounts Payable'

/**
 * A live count shown on a nav entry. The key names the source; the sidebar
 * resolves it (`useNavBadges`), so this file stays static data.
 * `review-unread`: unseen approval requests for the signed-in user (A4).
 */
export type NavBadgeKey = 'review-unread'

/** One sidebar / palette entry. Visibility is decided by `canAccess(href, roles)`. */
export interface NavItem {
  href: string
  label: string
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  badge?: NavBadgeKey
}

export interface NavSection {
  title: string
  items: NavItem[]
  /**
   * Folded away until somebody opens it (plan 08 WP-VIS-02). Collapsing is not
   * removing: the heading stays and one click opens the section.
   */
  collapsedByDefault?: boolean
}

/** Single source for both sidebars and the command palette. */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Platform',
    items: [
      { href: '/', label: 'Command Briefing', icon: Icons.sparkles },
      { href: '/dashboard', label: 'Dashboard', icon: Icons.dashboard },
      { href: '/invoices', label: 'Invoice Explorer', icon: Icons.receipt },
      // Oversight of the reviewer queue (plan 04 WP-REV-07): admin and AP-Manager.
      { href: '/review', label: 'Approvals', icon: Icons.checkCircle, badge: 'review-unread' },
      { href: '/ai-coworker-hub', label: 'AI Coworker Hub', icon: Icons.bot },
      { href: '/data-manager', label: 'Data Manager', icon: Icons.layers },
      { href: '/tax-hub', label: 'Tax Hub', icon: Icons.shieldCheck },
      { href: '/contracts', label: 'Contract Hub', icon: Icons.scrollText },
    ],
  },
  {
    title: 'AI Intelligence',
    items: [
      { href: '/ai/policies', label: 'AI Policies', icon: Icons.brain },
      { href: '/ai/insights', label: 'AI Insights', icon: Icons.lightbulb },
      // Risk Control Tower (docs/forensics/03-frontend.md): admin and AP-Manager.
      { href: '/forensics', label: 'Internal Audit', icon: Icons.shield },
    ],
  },
  {
    title: 'Administration',
    collapsedByDefault: true,
    items: [
      { href: '/admin/users', label: 'User Management', icon: Icons.users },
      { href: '/admin/roles', label: 'Role Management', icon: Icons.shield },
      { href: '/admin/groups', label: 'Group Management', icon: Icons.network },
      { href: '/admin/sessions', label: 'User Sessions', icon: Icons.device },
      { href: '/admin/events', label: 'Login Events', icon: Icons.activity },
      { href: '/admin/audit', label: 'Audit Logs', icon: Icons.fileText },
      { href: '/admin/settings', label: 'Admin Settings', icon: Icons.settings },
    ],
  },
  {
    title: 'System',
    collapsedByDefault: true,
    items: [{ href: '/settings', label: 'Settings', icon: Icons.settings }],
  },
]

/** Sections filtered to what `roles` may open; empty sections are dropped. */
export function visibleNavSections(roles?: readonly string[] | null): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canAccess(item.href, roles)),
  })).filter((section) => section.items.length > 0)
}

/** The nav label for an exact href, or undefined when no entry has that href. */
export function labelForPath(path: string): string | undefined {
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((entry) => entry.href === path)
    if (item) return item.label
  }
  return undefined
}

/** The root crumb's label when `/` has no nav entry. */
export const ROOT_LABEL = 'Command Briefing'

/** Header breadcrumb with labels taken from the navigation (plan 06 WP-UX-01). */
export function breadcrumbsFor(pathname: string | null | undefined): Crumb[] {
  return breadcrumbTrail(pathname, (href) => crumbLabelFor(href) ?? labelForPath(href), ROOT_LABEL)
}
